import { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useGetList } from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { marketsForScope } from '../dataProvider/types';
import type {
  BlockSchedules,
  Market,
  PageSchema,
  RateRule,
  UserTier,
} from '../dataProvider';
import { useCatalog } from './useCatalog';
import { PagePreview } from './PagePreview';
import { ScheduleTimeline } from './ScheduleTimeline';
import { visibleBlocks } from './blockSchedule';
import { useStoreSlugRegionIndex } from './useStoreSlugRegionIndex';
import type { RegionScopeView } from '../rateRules/rateRuleScope';
import {
  projectStoreRate,
  type StoreRateProjection,
} from './storeRateProjection';
import {
  LIFECYCLE_COLOR,
  LIFECYCLE_LABEL,
  currentPublished,
  pageLifecycle,
} from './pageSchemaLifecycle';

/**
 * The live preview pane (D-page-schema-editor-8c, 8c-boost). A READ-ONLY structural-
 * honest preview of a page's composition. It renders, it NEVER mutates: no save, no
 * publish, no new capability (it reads what the editor role already reaches; a viewer
 * who cannot open the form cannot open the preview). It lives inside the gated edit
 * form alongside the composer (8a, unchanged).
 *
 * WHAT IT READS, a DRAFT-vs-PUBLISHED toggle over the two facets already on the row:
 *  - DRAFT = row.blocks (what the editor is composing, what publish would push).
 *  - PUBLISHED = currentPublished(row).blocks (the latest history entry, what the
 *    apps serve), or "not yet published" when the page has never published.
 * It reuses the 8b pageLifecycle derivation (draft_only / published_clean /
 * draft_ahead) for the per-market state chip; it does NOT recompute draft-ahead.
 *
 * BOTH UK AND US, the same pageId's per-market rows SIDE BY SIDE. A PageSchema row is
 * market-specific, so a pageId like home exists as a home/uk row AND a home/us row
 * (they differ). The pane fetches the sibling rows for this pageId across the markets
 * the viewer's scope permits (marketsForScope(activeRegion)) and shows one column per
 * market. OPERATIONAL scoping gates it with NO new rule: a single-market (uk) viewer
 * sees only uk; a GLOBAL viewer sees uk and us side by side.
 *
 * RATE PROJECTION (8c-boost). Each store card shows a tier-labelled PROJECTED rate from
 * the one #64 resolver, resolved at the REGION grain via the slug + market index, for
 * the tier the toggle selects (default free, the honest baseline and the previewTier
 * precedent). It is a projection over today's rules, not the live per-click resolution.
 * The preview operates on the SAVED draft (record.blocks), like the publish panel; save
 * the draft to preview pending composer edits.
 */

type Facet = 'draft' | 'published';
type AsOfMode = 'authored' | 'asof';

const facetBlocks = (row: PageSchema, facet: Facet) =>
  facet === 'draft' ? row.blocks : (currentPublished(row)?.blocks ?? null);

/** The schedules for the chosen facet: the draft row's, or the published snapshot's (8d). */
const facetSchedules = (row: PageSchema, facet: Facet): BlockSchedules =>
  facet === 'draft'
    ? (row.blockSchedules ?? {})
    : (currentPublished(row)?.blockSchedules ?? {});

/** The shared resolver inputs the pane hands each per-market column. */
interface BoostInputs {
  resolveRegion: (slug: string, market: Market) => RegionScopeView | null;
  rules: RateRule[];
  now: number;
  tier: UserTier;
  ready: boolean;
}

/** The as-of view state: all-blocks-as-authored (default) or only-visible-at a time. */
interface AsOf {
  mode: AsOfMode;
  time: number;
}

const MarketColumn = ({
  row,
  facet,
  catalog,
  boost,
  asOf,
}: {
  row: PageSchema;
  facet: Facet;
  catalog: ReturnType<typeof useCatalog>;
  boost: BoostInputs;
  asOf: AsOf;
}) => {
  const lifecycle = pageLifecycle(row);
  // The per-market projection: resolve each slug against THIS column's market, then
  // project the region-level rate for the selected tier.
  const projection: StoreRateProjection = {
    resolve: (slug) => {
      const view = boost.resolveRegion(slug, row.market);
      return view
        ? projectStoreRate(view, boost.rules, boost.now, boost.tier)
        : null;
    },
    tier: boost.tier,
    ready: boost.ready,
  };
  // LAYERING: the facet picks the blocks AND the schedules; the as-of filter then
  // drops the blocks not visible at the chosen time (default shows them all).
  const blocks = facetBlocks(row, facet);
  const schedules = facetSchedules(row, facet);
  const shown =
    asOf.mode === 'asof' && blocks
      ? visibleBlocks(blocks, schedules, asOf.time)
      : blocks;
  return (
    <Box
      sx={{ flex: 1, minWidth: 260 }}
      data-testid={`preview-market-${row.market}`}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 1 }}
        useFlexGap
        flexWrap="wrap"
      >
        <Typography variant="subtitle1">
          {String(row.market).toUpperCase()}
        </Typography>
        <Chip
          size="small"
          variant={lifecycle === 'published_clean' ? 'filled' : 'outlined'}
          color={LIFECYCLE_COLOR[lifecycle]}
          label={LIFECYCLE_LABEL[lifecycle]}
        />
      </Stack>
      <PagePreview
        blocks={shown}
        catalog={catalog}
        projection={projection}
        testId={`preview-blocks-${row.market}`}
      />
      {blocks && (
        <ScheduleTimeline
          blocks={blocks}
          schedules={schedules}
          now={asOf.mode === 'asof' ? asOf.time : boost.now}
          testId={`preview-timeline-${row.market}`}
        />
      )}
    </Box>
  );
};

export const PreviewPane = ({
  record,
  now,
}: {
  record: PageSchema;
  /** Injected clock for the projection (tests pass a fixed now); defaults to real time. */
  now?: number;
}) => {
  const { activeRegion } = useRegion();
  const catalog = useCatalog();
  const index = useStoreSlugRegionIndex();
  const [facet, setFacet] = useState<Facet>('draft');
  // Default free: the honest baseline most members get, the previewTier precedent.
  const [tier, setTier] = useState<UserTier>('free');
  // The clock is fixed for the life of the pane (the conflict-preview asOf precedent).
  const [resolvedNow] = useState<number>(() => now ?? Date.now());
  // The as-of view (8d). Default 'authored' shows ALL blocks; 'asof' filters to the
  // blocks visible at the chosen time. The time defaults to the pane clock.
  const [asOfMode, setAsOfMode] = useState<AsOfMode>('authored');
  const [asOfTime, setAsOfTime] = useState<number>(resolvedNow);

  const markets = useMemo(
    () => marketsForScope(activeRegion ?? 'GLOBAL'),
    [activeRegion],
  );

  // The sibling rows for this pageId across the IN-SCOPE markets (the same getList
  // contract the list uses: pageId equality plus market membership). The current
  // market column always renders from `record`; other in-scope markets render from a
  // sibling row when one exists.
  const { data: siblings = [] } = useGetList<PageSchema>('pageSchemas', {
    pagination: { page: 1, perPage: 50 },
    sort: { field: 'market', order: 'ASC' },
    filter: { pageId: record.pageId, market: markets },
  });

  // The rules the projection resolves over (the same read the #64 conflict preview uses).
  const { data: rules = [] } = useGetList<RateRule>('rateRules', {
    pagination: { page: 1, perPage: 1000 },
  });

  const boost: BoostInputs = {
    resolveRegion: index.resolve,
    rules,
    now: resolvedNow,
    tier,
    ready: !index.isLoading,
  };

  const columns = useMemo(() => {
    const ordered = [
      record.market,
      ...markets.filter((m) => m !== record.market),
    ];
    return ordered
      .map((m) =>
        m === record.market
          ? record
          : siblings.find((r) => r.market === m && r.id !== record.id),
      )
      .filter((r): r is PageSchema => Boolean(r));
  }, [record, siblings, markets]);

  return (
    <Card sx={{ mb: 2 }} data-testid="preview-pane">
      <CardContent>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ mb: 1 }}
          useFlexGap
          flexWrap="wrap"
        >
          <Typography variant="h6" component="h2" sx={{ mr: 1 }}>
            Preview
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={facet}
            onChange={(_e, next: Facet | null) => next && setFacet(next)}
            aria-label="Preview facet"
          >
            <ToggleButton value="draft" data-testid="facet-draft">
              Draft
            </ToggleButton>
            <ToggleButton value="published" data-testid="facet-published">
              Published
            </ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={tier}
            onChange={(_e, next: UserTier | null) => next && setTier(next)}
            aria-label="Preview tier"
          >
            <ToggleButton value="free" data-testid="tier-free">
              Free
            </ToggleButton>
            <ToggleButton value="plus" data-testid="tier-plus">
              Plus
            </ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={asOfMode}
            onChange={(_e, next: AsOfMode | null) => next && setAsOfMode(next)}
            aria-label="Schedule view"
          >
            <ToggleButton value="authored" data-testid="asof-authored">
              As authored
            </ToggleButton>
            <ToggleButton value="asof" data-testid="asof-on">
              As of a time
            </ToggleButton>
          </ToggleButtonGroup>
          {asOfMode === 'asof' && (
            <TextField
              size="small"
              type="datetime-local"
              label="As of"
              value={new Date(asOfTime).toISOString().slice(0, 16)}
              onChange={(e) => {
                const t = Date.parse(e.target.value);
                if (!Number.isNaN(t)) setAsOfTime(t);
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'data-testid': 'asof-time' }}
            />
          )}
        </Stack>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 0.5 }}
          data-testid="preview-disclaimer"
        >
          This is a representation of the composition, not a pixel render of the
          app. The apps serve the latest published version.
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2 }}
          data-testid="preview-projection-note"
        >
          A projection over today&apos;s rules for a {tier} member, not the live
          per-click resolution. Winner takes one, rules do not stack. Channel
          specific boosts are out of a brand card&apos;s scope.
        </Typography>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          divider={
            <Box sx={{ borderLeft: '1px solid', borderColor: 'divider' }} />
          }
        >
          {columns.map((row) => (
            <MarketColumn
              key={row.id}
              row={row}
              facet={facet}
              catalog={catalog}
              boost={boost}
              asOf={{ mode: asOfMode, time: asOfTime }}
            />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};
